import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Popover } from '@amzn/storm-ui';
import { selectHeaderState, selectIsBackUpNav } from '../../store/slice';
import { RootState } from 'src/app/store';
import { IA2AccountDropdown } from './IA2AccountDropdown';
import { AccountInfo } from './AjaxFetchAccountInfo';
import { AccountDropdownWrapper } from './styles';
import { AccountSwitcherPublisher } from 'src/commons/utils/metrics';
import { toCohort, publishCohortCounter } from 'src/commons/utils/whaleCohort';
import { IA2AccountDropdownTrigger } from './IA2AccountDropdownTrigger';
import {
  useAccountDropdownContext,
  useAccountDropdownProviderTiming,
} from './AccountDropdownProvider';
import {
  publishMountToInteractive,
  publishAccountDropdownProviderMountToInteractive,
} from 'src/commons/utils/mountToInteractive';

interface AccountDropdownProps {
  /*
   * When adding hasConsoleBanner (https://sim.amazon.com/issues/CCX-15314)
   * adjust max height of AccountDropdownWrapper
   */
  currentAccountInfo: AccountInfo;
  isBackUpNav: boolean;
}

export const AccountDropdown: React.VFC<AccountDropdownProps> = ({
  currentAccountInfo,
  isBackUpNav,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { accountCounter, loadingStatus, isPrecomputed } =
    useAccountDropdownContext();
  const cohort = toCohort(isPrecomputed);
  const providerTiming = useAccountDropdownProviderTiming();
  const mountedAt = useRef<number>();
  const hasPublishedInteractiveLatency = useRef(false);
  if (mountedAt.current === undefined) {
    mountedAt.current = performance.now();
  }

  useEffect(() => {
    const isReady = loadingStatus === 'success' && accountCounter !== 0;
    const isError = loadingStatus === 'error';
    if ((!isReady && !isError) || hasPublishedInteractiveLatency.current) {
      return;
    }

    hasPublishedInteractiveLatency.current = true;
    const completedAt = performance.now();
    const latencyFrom = (startedAt: number) =>
      Math.round(completedAt - startedAt);
    const mountLatencyMs = latencyFrom(mountedAt.current!);

    publishMountToInteractive(
      AccountSwitcherPublisher,
      isError
        ? { failed: true, latencyMs: mountLatencyMs }
        : {
            failed: false,
            latencyMs: mountLatencyMs,
            isWhale: isPrecomputed,
          },
    );
    if (!isError && providerTiming?.claim()) {
      publishAccountDropdownProviderMountToInteractive(
        AccountSwitcherPublisher,
        {
          latencyMs: latencyFrom(providerTiming.mountedAt),
          isWhale: isPrecomputed,
        },
      );
    }
  }, [accountCounter, loadingStatus, cohort, isPrecomputed, providerTiming]);

  const currentMarketplaceId = currentAccountInfo.currentMarketplaceId;
  const advertisingAccountsDataTaktId =
    currentMarketplaceId === currentAccountInfo?.currentMarketplaceId
      ? 'aac-account-links-same-mp'
      : `aac-account-links-different-mp-${currentMarketplaceId}`;

  const handleToggle = () => {
    publishCohortCounter(
      AccountSwitcherPublisher,
      isOpen ? 'Close' : 'Open',
      cohort,
    );
    setIsOpen(!isOpen);
  };

  const handleClose = () => setIsOpen(false);

  return (
    <Popover
      isOpen={isOpen}
      autoFocus={true}
      trigger={
        <IA2AccountDropdownTrigger
          isOpen={isOpen}
          handleToggle={handleToggle}
        />
      }
      position="bottom"
      align="start"
      strategy="fixed"
      onCloseButtonClick={handleClose}
      spacing={0}
      padding="none"
      data-ccx-e2e-id="aac-account-dropdown-wrapper"
    >
      {!isBackUpNav && (
        <AccountDropdownWrapper>
          <IA2AccountDropdown
            advertisingAccountsDataTaktId={advertisingAccountsDataTaktId}
          />
        </AccountDropdownWrapper>
      )}
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => {
  const headerState = selectHeaderState(state);
  const isBackUpNav = selectIsBackUpNav(state);
  return {
    currentAccountInfo: headerState.currentAccountInfo as AccountInfo,
    isBackUpNav: isBackUpNav,
  };
};

export default connect(mapStateToProps)(AccountDropdown);
